import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { TrendingUp, TrendingDown, Users, Wallet, Receipt, AlertTriangle, ArrowUpRight, ArrowDownRight, Sparkles } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Cell, AreaChart, Area } from "recharts";
import { api, formatXAF, formatNumber } from "@/lib/api";
import { useEntity } from "@/lib/entity";
import { useOutletContext } from "react-router-dom";
import AdvancedKpis from "@/components/AdvancedKpis";

export default function Dashboard() {
  const { currentEntityId, currentEntity } = useEntity();
  const { openAI } = useOutletContext();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/dashboard/${currentEntityId}`)
      .then(({ data }) => setData(data))
      .finally(() => setLoading(false));
  }, [currentEntityId]);

  if (loading || !data) {
    return <SkeletonGrid />;
  }

  const k = data.kpis;
  const cards = [
    { label: "Chiffre d'affaires", value: k.ca, icon: TrendingUp, accent: "citron", delta: null },
    { label: "Charges", value: k.charges, icon: TrendingDown, accent: "neutral" },
    { label: "Résultat net", value: k.resultat_net, icon: Receipt, accent: k.resultat_net >= 0 ? "ok" : "bad" },
    { label: "Solde trésorerie", value: k.solde_tresorerie, icon: Wallet, accent: "citron" },
    { label: "Encaissements", value: k.encaissements, icon: ArrowUpRight, accent: "ok" },
    { label: "Décaissements", value: k.decaissements, icon: ArrowDownRight, accent: "warn" },
    { label: "Effectif actif", value: k.effectif_actif, icon: Users, accent: "neutral", isCount: true },
    { label: "Masse salariale", value: k.masse_salariale, icon: Users, accent: "neutral" },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="space-y-8"
      data-testid="dashboard-page"
    >
      {/* Header */}
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="overline text-citron mb-3">Aperçu exécutif</div>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tighter leading-none">
            {currentEntity?.name}
          </h1>
          <p className="text-obsidian-400 text-sm mt-2">{currentEntity?.sub}</p>
        </div>
        <button
          onClick={openAI}
          className="inline-flex items-center gap-2 px-4 h-10 bg-gradient-to-r from-citron/15 to-citron/5 border border-citron/40 rounded-sm hover:border-citron transition-all"
          data-testid="dashboard-ai-cta"
        >
          <Sparkles size={14} className="text-citron" />
          <span className="text-sm font-medium text-citron">Demander à l'IA d'analyser</span>
        </button>
      </div>

      {/* KPI grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map((c, i) => (
          <KpiCard key={c.label} {...c} index={i} />
        ))}
      </div>

      {/* Advanced KPIs (Phase 7 — sparklines + comparatifs) */}
      <AdvancedKpis entityId={currentEntityId} />

      {/* Per-entity (only for GROUPE) */}
      {data.per_entity && data.per_entity.length > 0 && (
        <section>
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-2xl font-bold tracking-tight">Soldes par entité</h2>
            <span className="overline">XAF</span>
          </div>
          <div className="card p-6">
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={data.per_entity} margin={{ top: 8, right: 8, left: 8, bottom: 8 }}>
                <CartesianGrid strokeDasharray="2 4" stroke="#222428" vertical={false} />
                <XAxis dataKey="name" stroke="#52525B" fontSize={10} tickLine={false} axisLine={false} />
                <YAxis stroke="#52525B" fontSize={10} tickLine={false} axisLine={false} tickFormatter={(v) => formatNumber(v / 1000) + " k"} />
                <Tooltip
                  cursor={{ fill: "rgba(214, 243, 47, 0.05)" }}
                  contentStyle={{
                    background: "#121416",
                    border: "1px solid #222428",
                    borderRadius: 2,
                    fontSize: 12,
                  }}
                  formatter={(v) => [formatXAF(v), "Solde"]}
                />
                <Bar dataKey="solde" radius={[2, 2, 0, 0]} minPointSize={2}>
                  {data.per_entity.map((e, idx) => (
                    <Cell key={idx} fill={e.color || "#D6F32F"} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </section>
      )}

      {/* Empty state hint */}
      {k.ca === 0 && k.charges === 0 && k.solde_tresorerie === 0 && (
        <div className="card p-8 border-dashed">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 grid place-items-center bg-citron/10 rounded-sm flex-shrink-0">
              <AlertTriangle size={18} className="text-citron" />
            </div>
            <div>
              <h3 className="font-heading text-lg font-bold mb-1">Aucune donnée pour le moment</h3>
              <p className="text-sm text-obsidian-400 leading-relaxed">
                Commencez par saisir vos premières écritures dans les modules <strong className="text-obsidian-100">Trésorerie</strong>, <strong className="text-obsidian-100">Comptabilité</strong> ou <strong className="text-obsidian-100">RH</strong>. Les KPIs s'actualiseront automatiquement.
              </p>
            </div>
          </div>
        </div>
      )}
    </motion.div>
  );
}

function KpiCard({ label, value, icon: Icon, accent, index, isCount }) {
  const colors = {
    citron: "text-citron",
    ok: "text-ok",
    warn: "text-warn",
    bad: "text-bad",
    neutral: "text-obsidian-100",
  };
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, delay: index * 0.04 }}
      className="card p-5 hover:bg-obsidian-850 transition-colors"
      data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <div className="flex items-start justify-between mb-4">
        <div className="overline">{label}</div>
        <Icon size={14} className="text-obsidian-500" />
      </div>
      <div className={`font-mono text-2xl font-semibold tabular tracking-tight ${colors[accent]}`}>
        {isCount ? formatNumber(value) : formatXAF(value)}
      </div>
    </motion.div>
  );
}

function SkeletonGrid() {
  return (
    <div className="space-y-8">
      <div className="h-16 w-72 bg-obsidian-900 rounded-sm animate-pulse" />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {[...Array(8)].map((_, i) => (
          <div key={i} className="h-28 bg-obsidian-900 rounded-sm animate-pulse" />
        ))}
      </div>
    </div>
  );
}
